import React, { useState, useRef, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import UserEditService from "../../../../../../service/UserEditService";
import GetInfoService from "../../../../../../service/GetInfoService";
import GetUserInfoService from "../../../../../../service/GetUserInfoService";
import localeText from "../../../../../../locale";
import { useToastContext } from "../../../../../../contexts/ToastContext/ToastContext";

const userEditService = new UserEditService();
const getInfoService = new GetInfoService();
const getUserInfoService = new GetUserInfoService();
const defaultUserPhoto =
  "https://res.cloudinary.com/dwpu3xryx/image/upload/c_fill,f_auto,g_face,h_80,r_max,w_80/user_avatar_onpaw0.jpg";

const ProfileSettingsForm = ({ user, setUserInfo }) => {
  const { showSuccessToast, showErrorToast } = useToastContext();
  const [firstname, setFirstname] = useState("");
  const [surname, setSurname] = useState("");
  const [username, setUsername] = useState("");
  const [usernameCheckIcon, setUsernameCheckIcon] = useState("");
  const [amountDaysToPickUp, setAmountDaysToPickUp] = useState();
  const [pickupOnSaturday, setPickupOnSaturday] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const usernameRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    setFirstname(user.firstname ?? "");
    setSurname(user.surname ?? "");
    setUsername(user.username ?? "");
    setAmountDaysToPickUp(user.amountDaysToPickUp ?? user.quantityOfDaysToPickUp ?? null);
    setPickupOnSaturday(!!(user.pickupOnSaturday ?? user.canRetriveOnSaturdays));
  }, [user]);

  async function checkUsername(val) {
    if (!val || val === user?.username) {
      setUsernameCheckIcon("");
      return;
    }
    setUsernameCheckIcon("pi pi-spin pi-spinner");
    try {
      const res = await getInfoService.getFindUsername(val);
      if (res.messageCode === 200001) {
        showErrorToast({ detail: "usernameNotAvailable" });
        setUsernameCheckIcon("pi pi-times");
      } else {
        setUsernameCheckIcon("pi pi-check");
      }
    } catch {
      setUsernameCheckIcon("");
    }
  }

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  async function uploadAvatar(file) {
    if (!file || !(file instanceof File)) return;
    if (file.size / 1024 / 1024 > 5) {
      showErrorToast({ detail: "fileSizeTooBig" });
      return;
    }
    setLoading(true);
    try {
      const res = await userEditService.postChangeProfilePicture(
        localStorage.getItem("__extraconomy_token"),
        file,
        localStorage.getItem("__extraconomy_username"),
      );
      if (res.messageCode !== 200002) {
        const msg = localeText("data")?.find((m) => m.code === res.messageCode)?.message;
        showErrorToast({ detail: msg || "error" });
      } else {
        showSuccessToast({ detail: "sucessfullyEditedProfile" });
        if (setUserInfo) {
          try {
            const token = localStorage.getItem("__extraconomy_token");
            const username = localStorage.getItem("__extraconomy_username");
            const userRes = await getUserInfoService.getUserInfo(token, username);
            if (userRes.messageCode === 200001 && userRes.result?.user) {
              setUserInfo(userRes.result.user);
            }
          } catch (err) {
            // getUserInfo failed; user may need to refresh to see new avatar
          }
        }
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      } else {
        showErrorToast({ detail: "unexpectedErrorUploadImg" });
      }
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const handleFileChange = (e) => {
    const rawFile = e.target.files?.[0];
    if (rawFile) {
      const fileCopy = new File([rawFile], rawFile.name, { type: rawFile.type });
      uploadAvatar(fileCopy);
    }
  };

  async function handleApply() {
    if (!username?.trim()) {
      showErrorToast({ detail: "usernameCantBeEmpty" });
      return;
    }
    setLoading(true);
    const payload = {
      username: (user?.username ?? username) || null,
      firstname: firstname || null,
      surname: surname || null,
      amountDaysToPickUp: amountDaysToPickUp ?? null,
      pickupOnSaturday,
      postalCode: user?.postalCode ?? null,
      country: user?.country ?? null,
      state: user?.state ?? null,
      city: user?.city ?? null,
      district: user?.district ?? null,
      address: user?.address ?? null,
      number: user?.number ?? null,
      internationalCode: user?.internationalCode ?? null,
      localCode: user?.localCode ?? null,
      phoneNumber: user?.phoneNumber ?? null,
    };
    payload.firstname = firstname || null;
    payload.surname = surname || null;
    payload.username = username || null;
    payload.amountDaysToPickUp = amountDaysToPickUp ?? null;
    payload.pickupOnSaturday = pickupOnSaturday;
    Object.keys(payload).forEach((k) => (payload[k] = payload[k] === "" ? null : payload[k]));
    try {
      const res = await userEditService.postChangeProfileInfo(
        localStorage.getItem("__extraconomy_token"),
        payload,
      );
      if (res.messageCode !== 200000) {
        const msg = localeText("data")?.find((m) => m.code === res.messageCode)?.message;
        showErrorToast({ detail: msg || "error" });
      } else {
        showSuccessToast({ detail: "sucessfullyEditedProfile" });
        if (setUserInfo && res.result) setUserInfo(res.result);
      }
    } catch (e) {
      showErrorToast({ detail: "unexpectedSystemBehaviour" });
    } finally {
      setLoading(false);
    }
  }

  if (!user) return null;

  return (
    <div>
      {/* <h2 className="text-lg font-semibold m-0 mb-1" style={{ color: "var(--theme-text)" }}>
        {localeText("settingsTabProfile") || "Configurações de perfil"}
      </h2>
      <p className="text-sm m-0 mb-3" style={{ color: "var(--theme-text-muted)" }}>
        {localeText("profileSettingsSubtitle")}
      </p> */}

      <div className="flex flex-column gap-3">
        <div className="flex align-items-center gap-3">
          <div
            className="border-circle overflow-hidden"
            style={{
              width: "5rem",
              height: "5rem",
              background: `url(${user.urlAvatar || defaultUserPhoto}) center/cover`,
            }}
          />
          <div className="flex-1">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            <div className="flex flex-column gap-2">
              <div className="flex flex-column gap-1">
                <div className="font-medium" style={{ color: "var(--theme-text)" }}>
                  {localeText("uploadImage")}
                </div>
                <div className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
                  Min 400x400px, PNG or JPEG
                </div>
              </div>

              <div>
                <Button
                  label={localeText("upload")}
                  style={{
                    backgroundColor: "var(--theme-background)",
                    color: "var(--theme-text-muted)",
                    border: "1px solid var(--theme-border)",
                  }}
                  className="p-button-outlined text-sm border-round-md font-medium"
                  loading={loading}
                  disabled={loading}
                  onClick={handleChooseFile}
                />
              </div>
            </div>
          </div>
        </div>
        <hr />
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("firstName")} *
          </label>
          <InputText
            value={firstname}
            onChange={(e) => setFirstname(e.target.value)}
            style={{ color: "var(--theme-text)" }}
            className="w-full p-no-outline border-round-md"
          />
        </div>
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("surname")} *
          </label>
          <InputText
            value={surname}
            onChange={(e) => setSurname(e.target.value)}
            style={{ color: "var(--theme-text)" }}
            className="w-full p-no-outline border-round-md"
          />
        </div>
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("username")} *
          </label>
          <span className="p-input-icon-right w-full block">
            <i className={usernameCheckIcon} />
            <InputText
              ref={usernameRef}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onBlur={(e) => checkUsername(e.target.value)}
              style={{ color: "var(--theme-text)" }}
              className="w-full p-no-outline border-round-md"
            />
          </span>
        </div>
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("title") || "Título"}
          </label>
          <InputText
            placeholder={localeText("titlePlaceholder") || "ex.: administrador"}
            style={{ color: "var(--theme-text)" }}
            className="w-full p-no-outline border-round-md"
            disabled
          />
        </div>
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("biographyOptional") || "Biografia (Opcional)"}
          </label>
          <InputText
            placeholder={localeText("describeYourself") || "Descreva-se..."}
            style={{ color: "var(--theme-text)" }}
            className="w-full p-no-outline border-round-md"
            disabled
          />
        </div>
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("quantityOfDaysToRetrive")}
          </label>
          <InputNumber
            value={amountDaysToPickUp}
            onValueChange={(e) => setAmountDaysToPickUp(e.value)}
            placeholder="0"
            style={{ color: "var(--theme-text)" }}
            className="w-full p-no-outline border-round-md"
          />
        </div>
        <div className="flex align-items-center gap-2">
          <Checkbox
            inputId="pickupSaturday"
            checked={pickupOnSaturday}
            onChange={(e) => setPickupOnSaturday(e.checked)}
          />
          <label
            htmlFor="pickupSaturday"
            className="text-sm"
            style={{ color: "var(--theme-text-muted)" }}
          >
            {localeText("canRetriveOnSaturdays")}
          </label>
        </div>
        <div className="flex gap-2 mt-2" style={{ height: "40px" }}>
          <Button
            label={localeText("cancel")}
            className="p-button-outlined border-round-md flex-1 text-sm font-medium"
            style={{ borderColor: "var(--theme-border)", color: "var(--theme-text-muted)" }}
          />
          <Button
            label={localeText("applyChanges") || localeText("confirmProfileChanges")}
            loading={loading}
            className="border-round-md flex-1 text-sm font-medium"
            style={{ backgroundColor: "var(--theme-primary)", color: "#fff", border: "none" }}
            onClick={handleApply}
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileSettingsForm;
